import { useCallback, useEffect, useRef } from "react";
import { logDurationEvent } from "../analytics";

const IDLE_TIMEOUT_MS = 3 * 60 * 1000;

export const useDurationTracker = (isOpen: boolean, event: string) => {
  const startTimeRef = useRef<number | null>(null);
  const lastActiveTimeRef = useRef<number | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startTracking = () => {
    const now = Date.now();
    startTimeRef.current = now;
    lastActiveTimeRef.current = now;
    resetIdleTimer();
  };

  const endTracking = () => {
    clearIdleTimer();
    if (startTimeRef.current && lastActiveTimeRef.current) {
      const durationSec = (lastActiveTimeRef.current - startTimeRef.current) / 1000;

      if (durationSec > 0) {
        logDurationEvent(event, durationSec);
      }
    }

    startTimeRef.current = null;
    lastActiveTimeRef.current = null;
  };

  const resetIdleTimer = () => {
    clearIdleTimer();
    idleTimeoutRef.current = setTimeout(() => {
      endTracking();
    }, IDLE_TIMEOUT_MS);
  };

  const clearIdleTimer = () => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  };

  const handleUserActivity = useCallback(() => {
    if (!startTimeRef.current) {
      const now = Date.now();
      startTimeRef.current = now;
      lastActiveTimeRef.current = now;
    }
    else {
      lastActiveTimeRef.current = Date.now();
    }
    resetIdleTimer();
  }, []);

  useEffect(() => {
    if (isOpen) {
      startTracking();
      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("keydown", handleUserActivity);
      window.addEventListener("mousedown", handleUserActivity);
    }
    else {
      endTracking();
    }

    return () => {
      endTracking();
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("mousedown", handleUserActivity);
    };
  }, [isOpen, handleUserActivity]);
};
